import React from "react";
import { Box, TableCell, TableRow } from "@mui/material";
import { DotLegalPageHeader, DotLegalTableWithControls, ITableHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../localization/useTranslation";
import { useMasterDataStyles } from "./MasterData.styles";
import { MasterDataViewModel } from "./MasterData.Types";
import { primaryArrowRight } from "../common/icons";
import { useUrlProvider } from "../useUrlProvider";
import { useMasterDataMapping } from "./MasterData.hooks";
import { useUserContext } from "../auth/userContextProvider/UserContextProvider";
import { useNavigate } from "react-router";
import { usePlanContext } from "../auth/planProvider/PlanProvider";
import { useAiFeatureAccess } from "../plan/aiFeatureGate/useAiFeatureAccess";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

export function MasterData() {
    const styles = useMasterDataStyles();
    const { translateString } = useTranslation();
    const urlProvider = useUrlProvider();
    const { permissions, showLegacyRiskAssessments } = useUserContext();
    const { platformFeatures, informationSecurityFeatures, vendorManagementFeatures, addonsFeatures } = usePlanContext();
    const { canUseAiQualityAssurance } = useAiFeatureAccess();
    const navigate = useNavigate();

    if (!permissions.canManageMasterData) {
        navigate(urlProvider.getForbiddenUrl());
    }

    const { redirectTo, getBreadCrumbs } = useMasterDataMapping();

    //Indsæt objekt med navn og URl sideløbende med at de forskellige stamdata sider bliver tilføjet. Tænker vi benytter urlProvider.
    var masterData = [
        { name: translateString("purpose"), url: urlProvider.getPurposesUrl() },
        { name: translateString("securityMeasures"), url: urlProvider.getSecurityMeasures() },
        { name: translateString("deletionPeriods"), url: urlProvider.getDeletionPeriods() },
        { name: translateString("processingActivityAreas"), url: urlProvider.getBusinessAreasUrl() },
        { name: translateString("dataCategories"), url: urlProvider.getDataCategories() },
        { name: translateString("dataSubjects"), url: urlProvider.getDataSubjectsUrl() },
        { name: translateString("nationalLaws"), url: urlProvider.getNationalLawsUrl() },
        { name: translateString("helpTexts"), url: urlProvider.getHelpTextsUrl() },
        { name: translateString("documentTypes"), url: urlProvider.getDocumentTypesUrl() },
        { name: translateString("contractTypes"), url: urlProvider.getContractTypesUrl() },
        { name: translateString("legalEntityTypes"), url: urlProvider.getLegalEntityTypesUrl() },
        { name: translateString("paymentTerms"), url: urlProvider.getPaymentTermsUrl() },
    ];

    masterData.push({ name: translateString("annualWheelActivityTemplates"), url: urlProvider.getAnnualWheelActivityTemplatesUrl() });

    if (permissions.incidentLogPermissions.read) {
        masterData.push({ name: translateString("incidentTypesMasterData"), url: urlProvider.getIncidentTypesUrl() });
        masterData.push({ name: translateString("incidentAffectedServicesMasterData"), url: urlProvider.getIncidentAffectedServicesUrl() });
    }

    if (platformFeatures.riskVersions && permissions.riskVersionPermissions.read && showLegacyRiskAssessments) {
        masterData.push({ name: translateString("riskVersions"), url: urlProvider.getRiskAssessmentVersionsUrl() });
    }

    if (vendorManagementFeatures.certifications || informationSecurityFeatures.certifications) {
        masterData.push({ name: translateString("certifications"), url: urlProvider.getCertificationsUrl() });
    }

    if (platformFeatures.annualWheelComplianceArea) {
        masterData.push({ name: translateString("areas"), url: urlProvider.getAreasUrl() });
    }

    if (platformFeatures.tags && permissions.canAccessTags) {
        masterData.push({ name: translateString("tags"), url: urlProvider.getTagsUrl() });
    }

    if (addonsFeatures.obligations) {
        masterData.push({ name: translateString("obligationTemplates"), url: urlProvider.getObligationTemplatesUrl() });
    }

    if (canUseAiQualityAssurance) {
        masterData.push({ name: translateString("bestPracticeDocumentTemplates"), url: urlProvider.getBestPracticeDocumentTemplatesUrl() });
    }

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <DotLegalPageHeader breadCrumbs={getBreadCrumbs()} />

            <TablePagePaper>
                <DotLegalTableWithControls
                    headers={getHeaders(translateString)}
                    getUserSpecificPageLength={() => 30}
                    hideRowsPerPage
                    internalScroll
                    hasOverflowMenu
                    defaultOrderBy={"name"}
                    defaultOrder={"asc"}
                    isLoading={false}
                    labelRowsPerPage={translateString("showEntites")}
                    labelSearch={translateString("search")}
                    noOptionsLabel={translateString("noOptions")}
                    paginationLabelOf={translateString("labelOf")}
                    emptyText={translateString("noData")}
                    data={masterData}
                    renderRow={(row, i) => (
                        <TableRow onClick={() => redirectTo(row.url)} key={i} sx={styles.tableRow}>
                            <TableCell sx={styles.bold}>{row.name}</TableCell>
                            <TableCell align="right">
                                <Box sx={[styles.arrowContainer, styles.iconContainer]}>{primaryArrowRight}</Box>
                            </TableCell>
                        </TableRow>
                    )}
                />
            </TablePagePaper>
        </Box>
    );
}

function getHeaders(translateString: any) {
    let headers = Array<ITableHeader<MasterDataViewModel>>();
    headers.push({ property: "name", text: translateString("masterData"), align: "left", showOnMobile: true });
    return headers;
}
